import { getStops } from "../../app/api/getStops";
import { IStop } from "../../Orchestrator/Types/Interface/IStop";
import { getRuntimeConfig } from "../Config/RuntimeConfig";
import { getLocaleFromCookie } from "./getLocaleFromCookie";
import { getTranslatedMessage } from "./i18n";

// Shared between StopManager and the service-request actions that map
// API stops to dropdown values. Keeps the trim + length + typeof guards
// identical across consumers so adding a fourth caller doesn't drift.
export const getMappedStopCode = (stop: IStop, stopExternalCode: string): string | undefined => {
  const mappedCode = stop?.externalCodes?.[stopExternalCode];
  if (typeof mappedCode !== "string") {
    return undefined;
  }
  const normalizedCode = mappedCode.trim();
  return normalizedCode.length > 0 ? normalizedCode : undefined;
};

class StopManager {
  private static instance: StopManager;
  private stops: IStop[] | null = null;
  private stopsMap: Map<string, IStop> = new Map();
  private fetchPromise: Promise<IStop[]> | null = null;

  private constructor() { }

  public static getInstance(): StopManager {
    if (!StopManager.instance) {
      StopManager.instance = new StopManager();
    }
    return StopManager.instance;
  }


  public async fetchStops(): Promise<IStop[]> {
    if (this.stops) return this.stops;
    if (this.fetchPromise) return this.fetchPromise;
    // Set fetchPromise synchronously (before any await) so concurrent callers
    // coalesce instead of racing past the guard and firing duplicate requests.
    this.fetchPromise = (async () => {
      const config = await getRuntimeConfig();
      const stopExternalCode = config.NEXT_PUBLIC_STOP_EXTERNAL_CODE;
      if (!stopExternalCode) {
        this.stops = [];
        this.stopsMap = new Map();
        return this.stops;
      }
      const res = await getStops(false);
      let stops = res.answer?.stops || [];
      if (res.status === "ok" && Array.isArray(stops)) {
        this.stops = stops.filter((stop) => Boolean(getMappedStopCode(stop, stopExternalCode)));
        this.stopsMap = new Map(
          this.stops.map((stop) => [getMappedStopCode(stop, stopExternalCode)!, stop])
        );
        return this.stops;
      } else {
        this.stops = [];
        return this.stops;
      }
    })();
    return this.fetchPromise;
  }

  public getStopNameById(stopId: string, defaultValue?: string): string {
    if (!this.stops) return defaultValue || stopId; // return stopId if stops are not loaded
    const stop = this.stopsMap.get(stopId);

    const currentLocale = getLocaleFromCookie();
    // English and Arabic: return the API `defaultName` directly (not a local
    // translation key). The backend localizes `defaultName` server-side from the
    // Accept-Language header (sent by ApiRequest, and part of the stops cache key),
    // so Arabic users receive the Arabic name straight from the API.
    if (currentLocale === "en" || currentLocale === "ar") {
      return stop?.defaultName || defaultValue || stopId;
    }

    return stop ? getTranslatedMessage(stopId, defaultValue || stop.defaultName) : defaultValue || stopId;
  }

}

const stopManager = StopManager.getInstance();
export default stopManager;
